// Pure helpers for the desktop Local Recordings manager. No React, no IndexedDB —
// the hook reads lightweight metadata rows from the store and hands them here so
// this logic is unit-testable without a fake IDB. Mirrors the codebase pattern of
// extracting pure recording logic (audioStitch, recordingRecoveryDismissState).

import { MAX_REBUILD_AGE_MS } from "./recordingRebuildGate";

// Keep the default in sync with WARN_AFTER_ERRORS in useIndexDb.js (passed in by
// the hook, defaulted here only so the util is usable standalone in tests).
const DEFAULT_WARN_AFTER_ERRORS = 3;

export function deriveRecordingStatus(errorsCount, warnAfterErrors = DEFAULT_WARN_AFTER_ERRORS) {
  const n = errorsCount || 0;
  if (n >= warnAfterErrors) return "error";
  if (n > 0) return "retrying";
  return "pending";
}

const toMs = (value) => {
  if (value == null) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

// What a row says about its own patient and about an identity park (encounter ids
// are recycled, so the row's stored label beats any lookup by chatId). Fields are
// left undefined when absent so rows queued before capture keep their old shape.
const identityFields = (row) => ({
  patientLabel: row.patientLabel || undefined,
  parked: row.parked === "identity" ? "identity" : undefined,
  parkReason: row.parked === "identity" ? row.parkReason || undefined : undefined,
  parkTarget: row.parked === "identity" ? row.parkTarget || undefined : undefined,
});

// segmentSecs: nominal length of one captured segment (~10s, useRecorder's
// SEGMENT_MS). Used only to estimate a segmented recording's duration for display.
// includeFinalless: also return segment groups whose FINAL segment never arrived
//   — a recording the tab slept through, a suspended machine, an interrupted
//   capture. Off by default so every existing caller is unchanged.
// excludeRecordingIds: recordings to drop whatever their shape. The reason
//   finalless groups were hidden in the first place is the recording being made
//   RIGHT NOW, whose segments arrive one per ~10s and which must never be acted
//   on — but that is an identity question the caller can answer (the heartbeat /
//   active-recording marker), not something the row shape can. Hiding EVERY
//   unfinished recording to catch the one live one meant a clinician whose queue
//   was entirely finalless saw an empty list with hours of audio behind it.
export function groupRecordings(
  rows,
  { warnAfterErrors = DEFAULT_WARN_AFTER_ERRORS, segmentSecs = 10, includeFinalless = false, excludeRecordingIds = [] } = {}
) {
  const excluded = new Set(excludeRecordingIds || []);
  const whole = [];
  const segmentsById = new Map();

  for (const row of rows || []) {
    if (row && row.kind === "segment") {
      // Group by recordingId. A segment with NO recordingId can't be safely
      // grouped with others — doing so would let Delete/Retry act on unrelated
      // recordings — so it gets its own singleton group keyed by its primary key.
      const groupKey = row.recordingId != null ? "id:" + row.recordingId : "key:" + row.key;
      const group = segmentsById.get(groupKey) || {
        recordingId: row.recordingId,
        chatId: row.chatId,
        orgId: row.orgId,
        keys: [],
        errorsCount: 0,
        dateMs: Infinity,
        hasFinal: false,
        total: 0,
        patientLabel: undefined,
        parked: undefined,
        parkReason: undefined,
        parkTarget: undefined,
      };
      group.keys.push(row.key);
      group.errorsCount = Math.max(group.errorsCount, row.errorsCount || 0);
      group.dateMs = Math.min(group.dateMs, toMs(row.startedAt != null ? row.startedAt : row.date));
      group.total = Math.max(group.total, row.total || 0);
      if (row.isFinal) group.hasFinal = true;
      if (group.chatId == null) group.chatId = row.chatId;
      if (group.orgId == null) group.orgId = row.orgId;
      // Any parked row parks the whole recording (the drain flags every row, but
      // older parks may have flagged only one).
      const idf = identityFields(row);
      if (!group.patientLabel && idf.patientLabel) group.patientLabel = idf.patientLabel;
      if (idf.parked && !group.parked) {
        group.parked = idf.parked;
        group.parkReason = idf.parkReason;
        group.parkTarget = idf.parkTarget;
      }
      segmentsById.set(groupKey, group);
    } else if (row) {
      if (row.recordingId != null && excluded.has(row.recordingId)) continue;
      // The real start time when the row carries it; older rows only have the
      // queue date, which is never earlier than the start.
      const dateMs = toMs(row.startedAt != null ? row.startedAt : row.date);
      whole.push({
        id: "w:" + row.key,
        recordingId: row.recordingId,
        chatId: row.chatId,
        // The org the capture was stamped with (see currentOrgId). Carried so the
        // modal can apply the drain's own org gate to an unfinished row's Retry.
        orgId: row.orgId,
        dateMs,
        errorsCount: row.errorsCount || 0,
        status: deriveRecordingStatus(row.errorsCount || 0, warnAfterErrors),
        isSegmented: false,
        isFinalless: false,
        segmentCount: 0,
        // Whole recordings carry an exact duration stamped at capture (older rows
        // predating the field leave this undefined → the modal just omits length).
        // Number.isFinite rejects NaN/Infinity (typeof NaN === "number") so a
        // corrupt value never renders as "NaNh".
        durationSecs: Number.isFinite(row.durationSecs) ? row.durationSecs : undefined,
        keys: [row.key],
        ...identityFields(row),
      });
    }
  }

  const segmented = Array.from(segmentsById.values())
    // Never the live capture, whichever shape it is in.
    .filter((g) => !(g.recordingId != null && excluded.has(g.recordingId)))
    // A group whose FINAL segment has arrived is a complete recording that is
    // failing to upload. One without is unfinished — surfaced only on request.
    .filter((g) => g.hasFinal || includeFinalless)
    .map((g) => ({
      id: g.recordingId != null ? "s:" + g.recordingId : "s:key:" + g.keys[0],
      recordingId: g.recordingId,
      chatId: g.chatId,
      orgId: g.orgId,
      dateMs: g.dateMs === Infinity ? 0 : g.dateMs,
      errorsCount: g.errorsCount,
      status: deriveRecordingStatus(g.errorsCount, warnAfterErrors),
      isSegmented: true,
      // Unfinished: no final segment ever landed, so nothing declared how long this
      // recording was meant to be and the drain cannot assemble it as a matter of
      // course (it needs the finalless recovery path). Callers use it to label the
      // row and to explain a duration that can only be a lower bound.
      isFinalless: !g.hasFinal,
      segmentCount: g.keys.length,
      // Estimated from the expected total segment count (fall back to how many are
      // actually stored) × the nominal per-segment length. On a finalless group
      // `total` is 0 — nothing declared it — so the stored count is all there is.
      durationSecs: (g.total || g.keys.length) * segmentSecs,
      keys: g.keys,
      patientLabel: g.patientLabel,
      parked: g.parked,
      parkReason: g.parkReason,
      parkTarget: g.parkTarget,
    }));

  return [...whole, ...segmented].sort((a, b) => b.dateMs - a.dateMs);
}

// "45s" / "12m 30s" / "3h 15m". Shared by the Local Recordings manager and the
// sign-in backlog alert so a length never reads two different ways.
export function formatRecordingDuration(secs) {
  if (!Number.isFinite(secs) || secs < 0) return "";
  const s = Math.round(secs);
  if (s < 60) return s + "s";
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM ? `${h}h ${remM}m` : `${h}h`;
}

// Total audio a grouped recording list represents, in seconds. A segmented row
// carries an ESTIMATE (segment count x nominal length) and an unfinished one is a
// lower bound — nothing ever declared how long it was meant to be — so every
// consumer must present this as approximate.
export function queuedAudioSecs(list) {
  return (list || []).reduce((sum, r) => sum + (Number.isFinite(r?.durationSecs) ? r.durationSecs : 0), 0);
}

// Whether Retry can do anything for an UNFINISHED (finalless) recording. The drain
// only rebuilds one through the finalless recovery path, and that path is gated —
// the same gates shouldRebuildFinalless applies, minus the ones that resolve
// themselves with time (the live-capture / heartbeat / lock checks):
//   - the kill-switch (`ff_recordingAutoRecover` = "0") is off;
//   - the native shell never rebuilds in the WebView (the app owns the capture);
//   - the recording was captured under another org, or under none (legacy rows
//     predate the stamp) — the drain fails CLOSED on that, every pass, silently
//     (no cross-org upload, spec §14C), and nothing resolves it: signing in under
//     the other org is a different session, and this row still shows here;
//   - the recording is older than MAX_REBUILD_AGE_MS (48h, the server's own sweep
//     window) — past that the drain declines it every pass, silently.
// Offering Retry on such a row is a button that does nothing: the audit found the
// modal rendering it for every row. When this is false the row should say so and
// point at the two things that DO still work — save the audio, or delete it.
//
// `orgId` is the row's stamp and `currentOrgId` the session's (utils/currentOrgId);
// the rule is the drain's own: `!!orgId && orgId === currentOrgId()`.
//
// An unknown date (0 / non-finite) is not evidence of age and does not block, which
// is also how the drain treats a group with no dated rows (oldestSegmentAge = 0).
export function canRetryFinalless({
  dateMs,
  now = Date.now(),
  autoRecoverEnabled = true,
  isNativeShell = false,
  orgId,
  currentOrgId,
} = {}) {
  if (isNativeShell) return false;
  if (!autoRecoverEnabled) return false;
  if (!orgId || orgId !== currentOrgId) return false;
  if (!Number.isFinite(dateMs) || dateMs <= 0) return true;
  return now - dateMs <= MAX_REBUILD_AGE_MS;
}
